import React, { useState, useEffect, useRef } from "react";

/**
 * ServiceSearchDropdown
 *
 * Props:
 *   allService   – full array of { id, serviceName, category } from parent
 *   serviceId    – currently selected service id (string or number)
 *   onChange     – ({ serviceId, serviceName, category }) => void
 *   error        – boolean — red border when true
 *   placeholder  – string (default "Search service...")
 *   disabled     – boolean
 *
 * Behaviour (matches screenshot):
 *   • Single text input with a ▼ chevron
 *   • Typing filters ALL services by serviceName (case-insensitive)
 *   • Results grouped by category — category shown as a grey uppercase header
 *   • Clicking a result sets the service AND auto-derives the category
 *   • No separate Category dropdown needed
 */
const ServiceSearchDropdown = ({
  allService = [],
  serviceId,
  onChange,
  error = false,
  placeholder = "Search service...",
  disabled = false,
}) => {
  // The text shown in the input box
  const [query, setQuery] = useState("");
  // Whether the dropdown is open
  const [open, setOpen] = useState(false);
  // Highlighted index for keyboard nav
  const [highlighted, setHighlighted] = useState(0);

  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // ── Sync display label when serviceId changes externally ──
  useEffect(() => {
    if (!serviceId) {
      setQuery("");
      return;
    }
    const found = allService.find(
      (s) => String(s.id) === String(serviceId)
    );
    if (found) setQuery(found.serviceName);
  }, [serviceId, allService]);

  // ── Filter services ──
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allService;
    return allService.filter((s) =>
      s.serviceName?.toLowerCase().includes(q)
    );
  }, [query, allService]);

  // ── Group filtered results by category ──
  const grouped = React.useMemo(() => {
    const map = {};
    filtered.forEach((s) => {
      const cat = s.category || "Other";
      if (!map[cat]) map[cat] = [];
      map[cat].push(s);
    });
    return map; // { "Minor Surgery": [...], "Dermatology": [...] }
  }, [filtered]);

  // Flat list of selectable items (for keyboard nav index)
  const flatItems = React.useMemo(() => filtered, [filtered]);

  // ── Close on outside click ──
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        // If query doesn't match selected, revert
        if (serviceId) {
          const found = allService.find(
            (s) => String(s.id) === String(serviceId)
          );
          if (found) setQuery(found.serviceName);
          else setQuery("");
        } else {
          setQuery("");
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [serviceId, allService]);

  // ── Keyboard navigation ──
  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        setHighlighted(0);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flatItems[highlighted]) selectItem(flatItems[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const selectItem = (service) => {
    onChange({
      serviceId: service.id,
      serviceName: service.serviceName,
      category: service.category || "",
    });
    setQuery(service.serviceName);
    setOpen(false);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setOpen(true);
    setHighlighted(0);
  };

  const handleInputClick = () => {
    if (!disabled) {
      setOpen(true);
      setHighlighted(0);
    }
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onChange({ serviceId: "", serviceName: "", category: "" });
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  };

  const categoryEntries = Object.entries(grouped);

  return (
    <>
      <style>{dropdownCss}</style>
      <div
        ref={wrapRef}
        className={`ssd-wrap${open ? " ssd-open" : ""}${error ? " ssd-error" : ""}${disabled ? " ssd-disabled" : ""}`}
      >
        {/* ── Input row — matches screenshot: text input + ▼ chevron ── */}
        <div className="ssd-input-row" onClick={handleInputClick}>
          <input
            ref={inputRef}
            className="ssd-input"
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
          />
          {/* Clear ✕ when something is selected */}
          {serviceId && !disabled && (
            <button type="button" className="ssd-clear" onClick={clearSelection}>
              ×
            </button>
          )}
          {/* ▼ chevron — matches screenshot */}
          <span className={`ssd-chevron${open ? " ssd-chevron-up" : ""}`}>▼</span>
        </div>

        {/* ── Dropdown panel — matches screenshot layout ── */}
        {open && (
          <div className="ssd-panel" ref={listRef}>
            {categoryEntries.length === 0 ? (
              <div className="ssd-empty">No services found</div>
            ) : (
              categoryEntries.map(([category, services]) => {
                // compute flat offset for keyboard highlight
                let flatOffset = 0;
                const catKeys = Object.keys(grouped);
                for (const c of catKeys) {
                  if (c === category) break;
                  flatOffset += grouped[c].length;
                }

                return (
                  <div key={category} className="ssd-group">
                    {/* Category header — matches screenshot: grey uppercase "MINOR SURGERY" */}
                    <div className="ssd-group-header">{category}</div>

                    {services.map((service, idx) => {
                      const globalIdx = flatOffset + idx;
                      const isSelected = String(service.id) === String(serviceId);
                      const isHl = highlighted === globalIdx;

                      return (
                        <div
                          key={service.id}
                          className={`ssd-option${isSelected ? " ssd-selected" : ""}${isHl ? " ssd-highlighted" : ""}`}
                          onMouseDown={(e) => {
                            e.preventDefault(); // prevent blur before click
                            selectItem(service);
                          }}
                          onMouseEnter={() => setHighlighted(globalIdx)}
                        >
                          {/* Highlight matching text */}
                          <HighlightMatch text={service.serviceName} query={query} />
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </>
  );
};

// ── Highlight matching portion of text ──
const HighlightMatch = ({ text, query }) => {
  if (!query.trim()) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(query.trim().toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <mark className="ssd-mark">{text.slice(idx, idx + query.trim().length)}</mark>
      {text.slice(idx + query.trim().length)}
    </span>
  );
};

// ── CSS — pixel-perfect match to screenshot ──
const dropdownCss = `
  /* Wrapper */
  .ssd-wrap {
    position: relative;
    width: 100%;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  }

  /* Input row — matches screenshot: white box with ▼ */
  .ssd-input-row {
    display: flex;
    align-items: center;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: white;
    cursor: text;
    transition: all 0.2s;
    overflow: hidden;
  }
  .ssd-wrap.ssd-open .ssd-input-row,
  .ssd-input-row:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  .ssd-wrap.ssd-error .ssd-input-row {
    border-color: #ef4444;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }
  .ssd-wrap.ssd-disabled .ssd-input-row {
    background: #f8fafc;
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* Text input inside the row */
  .ssd-input {
    flex: 1;
    padding: 10px 12px;
    border: none;
    outline: none;
    font-size: 14px;
    color: #1a1a1a;
    background: transparent;
    font-family: inherit;
    cursor: text;
    min-width: 0;
  }
  .ssd-input::placeholder { color: #94a3b8; }
  .ssd-input:disabled { cursor: not-allowed; }

  /* Clear button ✕ */
  .ssd-clear {
    border: none;
    background: none;
    color: #94a3b8;
    font-size: 18px;
    padding: 0 4px;
    cursor: pointer;
    line-height: 1;
    flex-shrink: 0;
    transition: color 0.15s;
  }
  .ssd-clear:hover { color: #475569; }

  /* ▼ chevron — matches screenshot */
  .ssd-chevron {
    padding: 0 12px;
    color: #64748b;
    font-size: 11px;
    flex-shrink: 0;
    transition: transform 0.2s;
    pointer-events: none;
    user-select: none;
  }
  .ssd-chevron.ssd-chevron-up {
    transform: rotate(180deg);
  }

  /* Dropdown panel — matches screenshot: white card, shadow, rounded */
  .ssd-panel {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    z-index: 9999;
    max-height: 260px;
    overflow-y: auto;
    font-size: 14px;
  }
  .ssd-panel::-webkit-scrollbar { width: 6px; }
  .ssd-panel::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 3px; }

  /* Empty state */
  .ssd-empty {
    padding: 16px;
    color: #94a3b8;
    text-align: center;
    font-size: 13px;
  }

  /* Group wrapper */
  .ssd-group {
    padding: 6px 0;
    border-bottom: 1px solid #f1f5f9;
  }
  .ssd-group:last-child { border-bottom: none; }

  /* Category header — matches screenshot: grey, uppercase, small caps */
  .ssd-group-header {
    padding: 6px 16px 4px;
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    user-select: none;
  }

  /* Option row — matches screenshot: "Cyst removal" indented under header */
  .ssd-option {
    padding: 9px 16px 9px 20px;
    cursor: pointer;
    color: #334155;
    font-size: 14px;
    transition: background 0.1s;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ssd-option:hover,
  .ssd-option.ssd-highlighted {
    background: #f0f9ff;
    color: #1a1a1a;
  }
  .ssd-option.ssd-selected {
    background: #eff6ff;
    color: #2563eb;
    font-weight: 500;
  }

  /* Highlighted match text — yellow mark like the screenshot */
  .ssd-mark {
    background: transparent;
    color: #2563eb;
    font-weight: 600;
    padding: 0;
  }
`;

export default ServiceSearchDropdown;